import { Button, Col, Row, message } from "antd";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { _adr } from "../../../address";
import { useAPIPublic } from "../../../tools/api";
import { globalValue } from "../../../reducer/serviceReducerGlobala";
import { isEmpty } from "../../../tools/tools";
import IncressCharge from "../incressCharge/charge";
import Loading from "../loader/loading";
import ShopPage, { toman } from "./ShopPage";

/**
 * Pre-invoice paid from the dealer wallet (charge, package, bill).
 *
 * stateKey     redux key that keeps the order while the dealer tops up the wallet at the bank; when the bank
 *              sends them back (?localInvoiceId=…) the order is restored from it and the top-up modal reopens
 * invoice      (order, wallet) => { details, rows: [[label, amount]], walletDebit } — each page keeps its own maths
 * pay          (order) => response of the purchase service (null = failed, the service already showed why)
 * finalPath    receipt route; finalState(response, order) is passed to it
 * chargePlanId plan id of the wallet top-up
 */
const WalletPrefactor = ({ title, prefix, steps, simLabel, stateKey, invoice, pay, finalPath, finalState, backPath, chargePlanId, showPurchase = true }) => {
	const nav = useNavigate();
	const dispatch = useDispatch();
	const location = useLocation();
	const [searchParam] = useSearchParams();
	const another = useSelector((state) => state?.another);
	const [messageApi, contextHolder] = message.useMessage();
	const bankRef = useRef(null);

	// back from the bank: restore the order saved before leaving and resume the top-up
	const [resume] = useState(() => {
		const id = searchParam.get("localInvoiceId");
		return isEmpty(id) ? null : id;
	});
	const [order] = useState(() => (resume ? another?.[stateKey] : location?.state));
	const [openCharge, setOpenCharge] = useState(!!resume);
	const [localInvoiceId, setLocalInvoiceId] = useState(resume ?? "");
	const [isLoading, setIsLoading] = useState(false);

	const wallet = another?._currentUser?.walletAmount;
	const { details, rows, walletDebit } = invoice(order, another?._currentUser);
	const enough = wallet - walletDebit >= 0;

	// the gateway answers with an auto-posting bank form; an empty answer means the modal was closed
	const handleCallBackCharge = (value) => {
		try {
			if (value?.length > 0) {
				dispatch(globalValue({ [stateKey]: { ...order } }));
				bankRef.current.innerHTML = value;
				document.querySelector('form[action="https://asan.shaparak.ir"]').submit();
			} else {
				setLocalInvoiceId("");
				setOpenCharge(false);
			}
		} catch (error) {
			console.error(error);
		}
	};

	const handleSubmit = async () => {
		setIsLoading(true);
		try {
			if (enough) {
				const response = await pay(order);
				if (response) nav(_adr.Root + finalPath, { state: finalState(response, order) });
			} else {
				messageApi.open({ type: "error", content: "موجودی کیف پول کافی نمی باشد." });
			}
		} catch (error) {
			console.error(error);
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<>
			{contextHolder}
			<ShopPage
				title={title}
				prefix={prefix}
				steps={steps}
				step={1}
				purchase={showPurchase ? order?.purchase : null}
				footer={
					<>
						<Button type="primary" className="btn-normal btn-aptel" danger onClick={handleSubmit}>
							مرحله بعد
						</Button>
						<Button type="default" className="btn-normal" onClick={() => nav(_adr.Root + backPath)}>
							بازگشت
						</Button>
					</>
				}>
				<div className="shop-invoice">
					<Row gutter={[16, 16]}>
						<Col span={14} xl={14} lg={14} md={24} sm={24} xs={24}>
							<div className="inv-card">
								<h3>صورت حساب مشتری</h3>
								<div className="inv-body">
									<div className="inv-sim">
										<span className="inv-sim-icon">
											<i className="icon-simcard"></i>
										</span>
										<div>
											<small>{simLabel} با شماره</small>
											<strong dir="ltr">{order?.msisdn}</strong>
										</div>
									</div>
									{details}
								</div>
							</div>
						</Col>
						<Col span={10} xl={10} lg={10} md={24} sm={24} xs={24}>
							<div className="inv-card">
								<h3>صورت حساب فروشنده</h3>
								<div className="inv-body">
									<ul className="inv-rows">
										{rows.map(([label, amount]) => (
											<li key={label}>
												<span>{label}</span>
												<span className="value">{toman(amount)}</span>
											</li>
										))}
									</ul>
									{enough ? (
										<div className="inv-pay success">
											<p>این مبلغ از کیف پول شما برداشت می‌شود</p>
											<h3>{toman(walletDebit)}</h3>
											<div className="inv-after">
												<span>موجودی پس از خرید</span>
												<b>{toman(wallet - walletDebit)}</b>
											</div>
										</div>
									) : (
										<div className="inv-pay error">
											<h3>اعتبار شما کافی نمی‌باشد</h3>
											<div>
												کسری اعتبار: <b>{toman(Math.abs(wallet - walletDebit))}</b>
											</div>
											<Button className="btn-normal btn-aptel" htmlType="button" onClick={() => setOpenCharge(true)}>
												افزایش اعتبار
											</Button>
										</div>
									)}
								</div>
							</div>
						</Col>
					</Row>
				</div>
			</ShopPage>

			<IncressCharge
				open={openCharge}
				planid={chargePlanId}
				msisdn={another?._currentUser?.telnoone}
				price={Math.abs(wallet - walletDebit)}
				callback={handleCallBackCharge}
				localInvoiceId={localInvoiceId}
			/>
			<Loading isOpen={isLoading} />
			<div ref={bankRef}></div>
		</>
	);
};

export default WalletPrefactor;

// tax percent from the server; 0 until it arrives (the pages show zeros until then, as before)
export const useTaxPercent = () => {
	const { queryTaxPercent } = useAPIPublic();
	const [tax, setTax] = useState(0);
	useEffect(() => {
		queryTaxPercent()
			.then((res) => res && setTax(res))
			.catch((error) => console.error(error));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);
	return tax;
};
